import cookies from '../../../node_modules/@buzzfeed/bf-utils/lib/cookies/index.js';
import uuid4 from '../../../node_modules/@buzzfeed/bf-utils/lib/uuid4/index.js';
import createMemoizedFunction from '../../../node_modules/micro-memoize/dist/micro-memoize.esm.js';
import { getBaseUrl } from '../../../node_modules/@buzzfeed/bf-utils/lib/query-string/index.js';
import { getLocalCookieDomain } from '../../../node_modules/@buzzfeed/bf-utils/lib/XDomainCookies/default-domains.js';
import { getUserUUID } from '../../../node_modules/@buzzfeed/bf-utils/lib/userid/index.js';
import pageSessionIds from '../../../node_modules/@buzzfeed/bf-utils/lib/page-session-ids/index.js';

/**
   * Creates a memoized layer function for retrieving common client metadata. The `url` value is
   * required in order to force new page session ids on url change. The url is stripped of hashes
   * to prevent new sessions from triggering on an anchor link click.
   * @param  {String} url - Full URL of the page on which the event occurred.
   * @return {Function} - A memoized layer function
   */
var commonClientMetadata = createMemoizedFunction(_ref => {
  let {
    url
  } = _ref;
  const sessionUuidCookieConfig = {
    name: 'bf-xdomain-session-uuid',
    days: 0.5 / 24,
    // Half hour
    domain: getLocalCookieDomain()
  };
  return createMemoizedFunction(() => {
    /**
     * If CET consumer is in an iframe, as long as the domain is the same as a parent,
     * client_uuid, client_session_id, random_user_uuid will be shared, as the domain shares cookies
     */
    const client_uuid = String(getUserUUID());
    const client_session_id = cookies.get(sessionUuidCookieConfig.name, uuid4());
    cookies.set({
      ...sessionUuidCookieConfig,
      value: client_session_id
    });

    // frameElement will return value if it is from within iframe on same domain.  In that case,
    //  use that as the window to synchronize values
    const win = window.frameElement ? window.parent : window;
    return {
      /**
       * Property order is intentional and purposefully not alphabetized. Defining the shorter
       * property names and values upfront help display a useful request preview in devtools
       * which helps debugging and QA'ing the requests.
       */
      client_uuid,
      client_session_id,
      random_user_uuid: cookies.get('user_uuid', 'unauthenticated'),
      referrer_uri: win.document.referrer,
      ...pageSessionIds(getBaseUrl(url))
    };
  }, {
    transformKey: JSON.stringify
  });
}, {
  /**
   * Using the base URI as the cache key stripped of query params and hashes. Otherwise, a new
   * session would be incorrectly triggered when:
   * - clicking on a section header anchor link
   * - actions on a page adds query params to the current url (using window.history.replaceState
   *   for example). Some quizes do this when results are shown, presumably to provide extra
   *   context in the event_uri value of events.
   */
  transformKey: _ref2 => {
    let [options] = _ref2;
    return getBaseUrl(options.url);
  }
});

export default commonClientMetadata;
